import React, { useEffect, useState } from 'react';
import { css, Theme } from '@emotion/react';
import { blocks } from '@birdi/theme';
import GreenCheckCircleSVG from '@birdi/icons/green-check-circle.svg';
import { useMetaSelector } from '../../../../meta/reducer';
import { config, stroot, subscription as subscriptionAPI } from '@birdi/js-sdk';
import { LoadingSpinner } from '@birdi/loading';
import { CancelPlanWarningModal } from '../cancel-plan-warning-modal';

// Theme color options
type ThemeColorOption =
  | 'lightSunConure'
  | 'lightFlora'
  | 'lightCanary'
  | 'lightOcean';
export type PaymentIntervalOption = 'annual' | 'monthly';

// Price object interface
export interface PriceOption {
  id: string;
  price: number;
  currency: string;
  interval: PaymentIntervalOption;
  gpCost?: number;
}

// Plan data interface
export interface PlanData {
  id: string;
  productName: string;
  prices: PriceOption[];
}

interface PlanOptionProps {
  title: string;
  icon: any;
  subtitle: string;
  description: string;
  features: string[];
  themeColor: ThemeColorOption;
  headerText: string;
  planData?: PlanData | null;
  displayInterval: PaymentIntervalOption;
  currency: string;
  customActionButton?: {
    url: string;
    label: string;
  };
  withHeader?: string;
  showIntervalToggle?: boolean;
  onIntervalChange?: (interval: PaymentIntervalOption) => void;
  annualBillingOnly?: boolean;
}

const IntervalTerminologyMapping = {
  annual: 'year',
  monthly: 'month',
};

export const PlanOption = (props: PlanOptionProps): JSX.Element => {
  const meta = useMetaSelector();
  const {
    title,
    icon: Icon,
    subtitle,
    description,
    features,
    themeColor,
    headerText,
    planData,
    displayInterval,
    currency,
    customActionButton,
    showIntervalToggle = false,
    onIntervalChange,
    annualBillingOnly = false,
  } = props;

  const [isCancelPlanWarningModalOpen, setIsCancelPlanWarningModalOpen] =
    useState(false);

  // Find the price option that matches the selected payment interval
  const priceOption = planData?.prices?.find(
    (p) => p.interval === IntervalTerminologyMapping[displayInterval],
  );

  // Format and display price - converting from cents to dollars
  const formatPrice = (
    price: number,
    currencyCode: string,
    interval: string,
  ) => {
    // Convert cents to dollars (divide by 100)
    let priceInDollars = price / 100;

    // If annual plan, divide by 12 to show monthly equivalent
    if (interval === 'year') {
      priceInDollars = priceInDollars / 12;
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceInDollars);
  };

  const isFreeProduct =
    title.toLowerCase() === 'free' ||
    planData?.productName?.toLowerCase().includes('free') ||
    (planData?.prices?.every((p) => p.price === 0) ?? false);

  // Generate price and currency text
  const getPriceWithCurrency = () => {
    if (isFreeProduct) {
      return subtitle;
    }

    if (priceOption) {
      const formattedPrice = formatPrice(
        priceOption.price,
        priceOption.currency || currency,
        priceOption.interval,
      );
      const currencyCode = (priceOption.currency || currency).toUpperCase();
      return `${formattedPrice} ${currencyCode}`;
    }

    return 'Contact us';
  };

  // Get formatted price with currency
  const priceText = getPriceWithCurrency();

  const getIntervalText = () => {
    if (isFreeProduct) return '';
    if (!priceOption || priceOption.price === 0) return '';

    return 'per month';
  };

  // Handle interval toggle
  const handleIntervalChange = (newInterval: PaymentIntervalOption) => {
    if (onIntervalChange) {
      onIntervalChange(newInterval);
    }
  };

  const isCurrentPlan = meta.organisation?.planData?.stripeProductName
    ?.toLowerCase()
    .includes(title.toLowerCase());

  const getPlanActionText = () => {
    if (customActionButton?.label) return customActionButton.label;

    if (!meta.organisation?.planData?.stripeProductName && isFreeProduct)
      return 'Select';
    if (!meta.organisation?.planData?.stripeProductName && !isFreeProduct)
      return 'Start 7-day free trial';

    const planOrder = ['Free', 'Plus', 'Growth', 'Ultimate'];

    const currentPlanIndex = planOrder.findIndex((plan) =>
      meta.organisation?.planData?.stripeProductName
        ?.toLowerCase()
        .includes(plan.toLowerCase()),
    );

    // Get this plan's index
    const thisPlanIndex = planOrder.findIndex(
      (plan) => title.toLowerCase() === plan.toLowerCase(),
    );

    // If current plan not found or this plan not found, default to "Upgrade"
    if (currentPlanIndex === -1 || thisPlanIndex === -1) return 'Upgrade';

    // If upgrading to any from free plan, show "Start 14-day free trial"
    if (
      meta.organisation?.planData?.stripeProductName
        ?.toLowerCase()
        .includes('free') &&
      thisPlanIndex > 0 &&
      thisPlanIndex > currentPlanIndex
    ) {
      return 'Start 7-day free trial';
    }

    return thisPlanIndex > currentPlanIndex ? 'Upgrade' : 'Downgrade';
  };

  const [isLoading, setIsLoading] = React.useState(false);

  const handleCancelConfirm = async () => {
    setIsCancelPlanWarningModalOpen(false);
    if (isLoading) return;

    try {
      setIsLoading(true);

      const selectedPriceId = priceOption?.id || '';

      const response = await subscriptionAPI.cancelSubscription(
        stroot('2mwuj'),
      );

      if (response.body.stripeSubscriptionId) {
        window.location.href = `/`;
      } else {
        window.location.href = response.body.redirectUrl;
      }
    } catch (error) {
      console.error('Error accessing Stripe portal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanActionClick = async (e: React.MouseEvent) => {
    if (customActionButton?.url) {
      window.location.href = customActionButton.url;
      return;
    }

    e.preventDefault();
    const movingToFreePlan =
      title.toLowerCase() === 'free' &&
      meta.organisation?.stripeProductName?.length &&
      meta.organisation?.stripeProductName.length > 0 &&
      !meta.organisation?.stripeProductName.toLowerCase().includes('free');
    if (movingToFreePlan) {
      setIsCancelPlanWarningModalOpen(true);
      return;
    }

    if (isLoading) return;

    try {
      setIsLoading(true);

      const selectedPriceId = priceOption?.id || '';

      const response = await subscriptionAPI.updatePlan(
        stroot('anhen7'),
        selectedPriceId,
        title.toLowerCase().includes('free'),
      );

      if (response.body.stripeSubscriptionId) {
        window.location.href = `/`;
      } else {
        window.location.href = response.body.redirectUrl;
      }
    } catch (error) {
      console.error('Error accessing Stripe portal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div css={blocks.planOptionsColumn}>
      <div
        css={(theme) => css`
          padding: 0.5em;
          border: solid 1px ${theme.mono30};
          border-radius: 1em;
          background-color: ${theme.mono0};
          &:hover {
            border: solid 1px ${theme.deepOcean};
            box-shadow: 1px 1px 5px 0 rgba(0, 0, 0, 0.3);
          }
          margin-top: 1em;
          padding-top: 2em;
          position: relative;
          display: flex;
          flex-direction: column;
          height: 100%;
        `}
      >
        <div
          css={(theme) => css`
            ${blocks.planTitleHolder(theme[themeColor])}
            position: absolute;
            top: -1.5em;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1;
            padding-top: 0.4em;
            padding-bottom: 0.4em;
          `}
        >
          <span css={blocks.planTitle}>{title}</span>
        </div>
        <div css={blocks.planSection1}>
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              margin: 0.5em 0;
              svg {
                font-size: 10em;
              }
            `}
          >
            <Icon />
          </div>

          <div
            css={css`
              font-size: 1.25rem;
              font-weight: bold;
              margin-top: 0.5em;
              text-align: center;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-wrap: wrap;
            `}
          >
            <span>{priceText}</span>
            {getIntervalText() && (
              <span
                css={css`
                  font-weight: normal;
                  margin-left: 4px;
                `}
              >
                {getIntervalText()}
              </span>
            )}
          </div>

          {/* Pricing interval section with consistent height */}
          <div
            css={css`
              height: 3em;
              margin-top: 1em;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            {showIntervalToggle && onIntervalChange && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  background-color: #f0f0f0;
                  border-radius: 1.25em;
                  padding: 0.25em;
                  width: fit-content;
                `}
              >
                <button
                  css={(theme) => css`
                    padding: 0.5em 0.75em;
                    border-radius: 1em;
                    border: none;
                    font-weight: bold;
                    font-size: 0.9em;
                    cursor: pointer;
                    background-color: ${displayInterval === 'annual'
                      ? theme.deepOcean
                      : 'transparent'};
                    color: ${displayInterval === 'annual'
                      ? 'white'
                      : theme.mono70};
                    transition: all 0.2s ease;
                  `}
                  onClick={() => handleIntervalChange('annual')}
                >
                  Annual
                </button>
                <button
                  css={(theme) => css`
                    padding: 0.5em 0.75em;
                    border-radius: 1em;
                    border: none;
                    font-weight: bold;
                    font-size: 0.9em;
                    cursor: pointer;
                    background-color: ${displayInterval === 'monthly'
                      ? theme.deepOcean
                      : 'transparent'};
                    color: ${displayInterval === 'monthly'
                      ? 'white'
                      : theme.mono70};
                    transition: all 0.2s ease;
                  `}
                  onClick={() => handleIntervalChange('monthly')}
                >
                  Monthly
                </button>
              </div>
            )}
            {annualBillingOnly && <span>Annual billing only</span>}
          </div>
        </div>

        <div
          css={(theme) => css`
            display: flex;
            flex-direction: column;
            margin: 1em;
            flex: 1;
            // min-height: 28em;
            // @media (max-width: ${breakpoints.xs}) {
            //   min-height: 10em;
            // }
          `}
        >
          {/* Button area with fixed height */}
          <div
            css={css`
              height: 3.5em;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 1.5em;
            `}
          >
            {isCurrentPlan && (
              <div
                onClick={handleCancelConfirm}
                css={(theme) => css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  text-align: center;
                  width: 13em;
                  height: 2.75em;
                  font-weight: bold;
                  color: ${theme.midnight};
                  border: solid 1px ${theme.deepOcean};
                  background-color: #CEE3FF;
                  border-radius: 0.25em;
                  cursor: pointer;
                `}
              >
                {isLoading ? (
                  <LoadingSpinner color="#0078C0" size="1em" />
                ) : (
                  'Manage current plan'
                )}
              </div>
            )}

            {!isCurrentPlan &&
              (customActionButton || (priceOption && priceOption.id)) && (
                <button
                  onClick={handlePlanActionClick}
                  css={(theme) => css`
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 13em;
                    height: 2.75em;
                    border-radius: 0.25em;
                    font-weight: bold;
                    color: ${theme.deepOcean};
                    border: solid 1.5px ${theme.deepOcean};
                    background-color: ${theme.mono0};
                    text-decoration: none;
                    transition: background-color 0.2s;
                    cursor: ${isLoading ? 'wait' : 'pointer'};
                    opacity: ${isLoading ? 0.7 : 1};
                    &:hover {
                      box-shadow: 1px 1px 5px 0 rgba(0, 0, 0, 0.3);
                      transform: translateY(-1px);
                    }
                  `}
                >
                  {isLoading ? (
                    <LoadingSpinner color="#0078C0" size="1em" />
                  ) : (
                    customActionButton?.label || getPlanActionText()
                  )}
                </button>
              )}
          </div>

          {/* Description section with fixed height */}
          <div
            css={css`
              height: 5.5em;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              margin-bottom: 0.5em;
            `}
          >
            <p
              css={css`
                margin: 0;
                font-size: 0.9rem;
                line-height: 1.4;
                overflow: hidden;
              `}
            >
              {description}
            </p>
          </div>

          {/* Features header section with fixed height */}
          <div
            css={css`
              height: 2em;
              display: flex;
              align-items: flex-end;
              margin-bottom: 0.25em;
            `}
          >
            <h3
              css={css`
                font-weight: bold;
                font-size: 0.9em;
                text-align: left;
                margin: 0;
              `}
            >
              {headerText}
            </h3>
          </div>

          {/* Features list - fixed height */}
          <div
            css={css`
              // flex: 1;
              font-size: 0.9rem;
              min-height: 10em;
              // background-color: red;
            `}
          >
            {features.map((feature) => (
              <div
                key={feature}
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: flex-start;
                  margin-top: 0.3em;
                  span {
                    display: block;
                    text-align: left;
                  }
                `}
              >
                <div
                  css={css`
                    color: #4caf50;
                    min-width: 1.25em;
                    width: 1.25em;
                    height: 1.25em;
                    margin-right: 0.5em;
                    margin-top: 0.25em;
                  `}
                >
                  <GreenCheckCircleSVG />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
        {/* <CancelPlanWarningModal
        open={isCancelPlanWarningModalOpen}
        setOpen={setIsCancelPlanWarningModalOpen}
        cancelConfirm={handleCancelConfirm}
      /> */}
      </div>
    </div>
  );
};
